import { useCallback, useEffect, useState } from "react";
import { X, PenLine, Ban, Images, QrCode, Sparkles, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { EmptyState, Spinner } from "@/components/common";
import ZoomViewer from "@/components/ZoomViewer";
import ReasonDialog from "@/components/ReasonDialog";
import { api, formatErr } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtMoney } from "@/lib/format";
import { fmtTime } from "@/components/QrScan";

const EX_ROWS = [["Vendor", "vendor_name"], ["VAT no.", "vendor_vat_number"], ["Invoice no.", "invoice_number"], ["Date", "invoice_date"], ["Before VAT", "before_vat"], ["VAT", "vat_amount"], ["Total", "total"], ["Item category", "item_category"]];
const exVal = (k, v, x) => (v == null || v === "" ? "—" : k === "invoice_date" ? `${fmtDate(v)}${x.invoice_time ? ` ${fmtTime(x.invoice_time)}` : ""}` : ["before_vat", "vat_amount", "total"].includes(k) ? fmtMoney(v) : v);

export const ExtractedPanel = ({ upload, onRefresh, compact }) => {
  const [busy, setBusy] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const x = upload.extracted;
  const st = rerunning ? "processing" : upload.extraction?.status;
  const rerun = async () => {
    setBusy(true);
    try {
      await api.post(`/client-uploads/${upload.id}/extract`); toast.success("Reading the bill again…"); setRerunning(true);
      setTimeout(async () => { await onRefresh?.(); setRerunning(false); }, 18000);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs" data-testid="extracted-panel">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="font-semibold text-slate-700">Details read from bill</span>
        {upload.qr && <span className="inline-flex items-center gap-1 rounded bg-emerald-100 text-emerald-800 px-1.5 py-0.5 font-medium" data-testid="extracted-qr-badge"><QrCode className="w-3 h-3" />ZATCA QR · {upload.qr_source === "scan" ? "scanned" : "from photo"}</span>}
        {st === "processing" && <span className="inline-flex items-center gap-1 text-sky-700" data-testid="extracted-processing"><Loader2 className="w-3 h-3 animate-spin" />AI reading…</span>}
        {st === "done" && <span className="inline-flex items-center gap-1 text-violet-700" data-testid="extracted-ai-done"><Sparkles className="w-3 h-3" />AI read{upload.ai?.confidence ? ` · ${upload.ai.confidence} confidence` : ""}</span>}
        {st === "failed" && <span className="text-red-600" data-testid="extracted-failed">AI reading failed</span>}
        {st !== "processing" && onRefresh && <button onClick={rerun} disabled={busy} className="ml-auto inline-flex items-center gap-1 text-slate-500 hover:text-slate-800" data-testid="extracted-rerun"><RefreshCw className="w-3 h-3" />Read again</button>}
      </div>
      {x ? (
        <div className={`grid ${compact ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2"} gap-x-4 gap-y-1`}>
          {EX_ROWS.map(([l, k]) => <div key={k} className="flex justify-between gap-2"><span className="text-slate-500">{l}</span><span className="num text-right text-slate-900 font-medium truncate" data-testid={`extracted-${k}`}>{exVal(k, x[k], x)}</span></div>)}
        </div>
      ) : <div className="text-slate-400">{st === "processing" ? "Details will appear shortly." : "No details could be read."}</div>}
      {upload.ai?.items?.length > 0 && (
        <details className="mt-2"><summary className="cursor-pointer text-slate-500" data-testid="extracted-items-toggle">{upload.ai.items.length} line item(s)</summary>
          <div className="mt-1 space-y-0.5">{upload.ai.items.map((it, i) => <div key={i} className="flex justify-between gap-2"><span className="truncate">{it.description}{it.quantity ? ` × ${it.quantity}` : ""}</span><span className="num">{it.amount != null ? fmtMoney(it.amount) : ""}</span></div>)}</div>
        </details>
      )}
    </div>
  );
};

export const UploadImages = ({ upload, testid = "upload-viewer", className = "h-[60vh]" }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => setIdx(0), [upload?.id]);
  if (!upload) return null;
  return (
    <div className="flex flex-col gap-2">
      <ZoomViewer src={upload.images[idx]?.hd_url} className={`rounded-md ${className}`} testid={testid} />
      {upload.images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto">
          {upload.images.map((im, i) => (
            <button key={im.thumb_path} type="button" onClick={() => setIdx(i)} data-testid={`${testid}-thumb-${i}`}
              className={`w-14 h-14 rounded overflow-hidden border-2 shrink-0 ${i === idx ? "border-emerald-600" : "border-transparent opacity-70"}`}>
              <img src={im.thumb_url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function PendingUploads({ clientId, onEnter, onCount }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const load = useCallback(async () => {
    const { data } = await api.get("/client-uploads", { params: { client_id: clientId, status: "pending" } });
    setRows(data);
    onCount?.(data.length);
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  if (!rows) return <Spinner />;
  return (
    <div className="panel p-5 fade-up" data-testid="pending-uploads">
      {!rows.length ? <EmptyState title="No pending bills" subtitle="Bills uploaded by the client from the portal appear here." testid="pending-empty" /> : (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
          {rows.map((u, i) => (
            <button key={u.id} type="button" onClick={() => setOpen(u)} data-testid={`pending-upload-${i}`}
              className="text-left group border border-slate-200 rounded-md overflow-hidden hover:border-emerald-400 transition-colors">
              <div className="relative aspect-square bg-slate-100">
                <img src={u.images[0]?.thumb_url} alt="" className="w-full h-full object-cover" />
                {u.images.length > 1 && <span className="absolute top-1.5 right-1.5 text-[10px] font-semibold bg-black/60 text-white rounded px-1.5 py-0.5 flex items-center gap-1"><Images className="w-3 h-3" />{u.images.length}</span>}
              </div>
              <div className="px-2.5 py-2">
                <div className="num text-xs font-medium text-slate-800">{fmtDate(u.bill_date)}</div>
                <div className="num text-[10px] text-slate-400">{u.upload_no} · {fmtDateTime(u.created_at)}</div>
                {u.extracted?.vendor_name && <div className="text-[11px] text-slate-700 truncate mt-0.5" data-testid={`pending-upload-vendor-${i}`}>{u.qr && <QrCode className="w-3 h-3 inline mr-1 text-emerald-700" />}{u.extracted.vendor_name}</div>}
                {u.extracted?.total != null && <div className="num text-[11px] font-semibold text-emerald-800">SAR {fmtMoney(u.extracted.total)}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="pending-viewer-modal">
          <div className="bg-white rounded-lg w-full max-w-4xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div><div className="font-semibold">{open.upload_no} · Bill date {fmtDate(open.bill_date)}</div>{open.note && <div className="text-xs text-slate-500">{open.note}</div>}</div>
              <Button size="sm" variant="ghost" onClick={() => setOpen(null)} data-testid="pending-viewer-close"><X className="w-4 h-4" /></Button>
            </div>
            <UploadImages upload={open} />
            <ExtractedPanel upload={open} compact onRefresh={async () => { const { data } = await api.get("/client-uploads", { params: { client_id: clientId, status: "pending" } }); setRows(data); setOpen((o) => data.find((d) => d.id === o?.id) || o); }} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="text-red-600" onClick={() => setRejecting(true)} data-testid="pending-reject-button"><Ban className="w-4 h-4 mr-1.5" />Reject</Button>
              <Button variant="outline" onClick={() => setOpen(null)} data-testid="pending-close-button">Close</Button>
              <Button className="bg-emerald-800 hover:bg-emerald-900" onClick={() => { onEnter(open, rows); setOpen(null); }} data-testid="pending-make-entry-button"><PenLine className="w-4 h-4 mr-1.5" />Make entry</Button>
            </div>
          </div>
        </div>
      )}
      <ReasonDialog open={rejecting} onOpenChange={setRejecting} title="Reject this upload?" destructive confirmLabel="Reject" testid="reject-upload-dialog"
        description="The client will see it as rejected with your reason. Nothing is deleted."
        onConfirm={async (reason) => {
          try { await api.post(`/client-uploads/${open.id}/reject`, { reason }); toast.success("Upload rejected"); setRejecting(false); setOpen(null); load(); } catch (e) { toast.error(formatErr(e)); }
        }} />
    </div>
  );
}
